<template>
  <div class="flex flex-col gap-3" data-testid="ds-attendance-queue-views">
    <div class="flex flex-wrap items-end gap-4">
      <div class="flex flex-col gap-1 text-left">
        <span class="text-sm font-bold text-textPrimary">Visão</span>
        <QueueViewSwitcher
          :model-value="view"
          :screen-key="DS_ATTENDANCE_QUEUE_SCREEN_KEY"
          :restore-preference="false"
          test-id="ds-attendance-view-switcher"
          @update:model-value="emit('update:view', $event)"
        />
      </div>
      <div v-if="view === 'kanban'" class="flex flex-col gap-1 text-left">
        <span class="text-sm font-bold text-textPrimary">Agrupar por</span>
        <div
          role="group"
          aria-label="Agrupar por"
          class="inline-flex gap-1 rounded-lg border border-lightGray30 bg-white p-1"
          data-testid="ds-attendance-group-by"
        >
          <button
            v-for="option in GROUP_BY_OPTIONS"
            :key="option.value"
            type="button"
            class="rounded-md px-3.5 py-1.5 text-sm font-medium"
            :class="
              option.value === groupBy
                ? 'bg-primary5 text-primary'
                : 'text-neutral50 hover:text-textPrimary'
            "
            :aria-pressed="option.value === groupBy"
            :data-testid="`ds-attendance-group-by-${option.value}`"
            @click="emit('update:groupBy', option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="view === 'kanban' ? kanbanInitialLoading : loading"
      class="flex flex-col gap-2"
      data-testid="ds-attendance-queue-loading"
    >
      <AuditoriumExceptionalitiesCardSkeleton v-for="n in skeletonCount" :key="`skeleton-${n}`" />
    </div>

    <ReportErrorState
      v-else-if="view === 'kanban' ? kanbanFailed : error"
      title="Não foi possível carregar os registros de presença"
      :message="view === 'kanban' ? KANBAN_ERROR_MESSAGE : error"
      @retry="emit('retry')"
    />

    <EmptyList v-else-if="view === 'kanban' && kanbanEmpty" :title="emptyTitle" />

    <div
      v-else-if="view === 'list'"
      class="flex flex-col gap-2"
      data-testid="ds-attendance-view-list"
    >
      <template v-if="listEntries.length">
        <div v-for="entry in listEntries" :key="entry.container.id" class="flex flex-col gap-1">
          <DsAttendanceSlaLine :container="entry.container" :now-ms="nowMs" />
          <DsAttendanceListCard :item="entry.item" @details="emit('details', $event)" />
        </div>
      </template>
      <EmptyList v-else :title="emptyTitle" />
    </div>

    <div
      v-else-if="view === 'table'"
      class="flex flex-col gap-2"
      data-testid="ds-attendance-view-table"
    >
      <QueueTable
        v-if="listRows.length"
        :rows="listRows"
        :columns="DS_ATTENDANCE_TABLE_COLUMNS"
        :row-key="rowKey"
        selectable
        :selected-ids="selectedIds"
        :is-row-selectable="isRowSelectable"
        :sort="deadlineSorted ? DEADLINE_SORT : null"
        @update:selected-ids="emit('update:selectedIds', $event)"
        @update:sort="onSort"
      >
        <template #cell-id="{ row }">
          <span class="font-bold text-primary30">#{{ row.id }}</span>
        </template>
        <template #cell-schedule="{ row }">
          <span class="block">{{ row.schedule?.name ?? 'Escala não informada' }}</span>
          <span class="block text-xs text-neutral50">{{
            row.healthUnit?.name ?? 'Unidade não informada'
          }}</span>
        </template>
        <template #cell-status="{ row }">
          <StatusBadge :status="row.status" />
        </template>
        <template #cell-deadline="{ row }">
          <template v-if="tableDeadlines.get(row.id)">
            <span class="block">{{ tableDeadlines.get(row.id)?.date }}</span>
            <span class="block text-xs text-neutral50">{{
              tableDeadlines.get(row.id)?.phase
            }}</span>
          </template>
          <span v-else>—</span>
        </template>
        <template #cell-slaState="{ row }">
          <ModuleViewSlaChip :sla="row.sla ?? null" :now-ms="nowMs" />
        </template>
        <template #actions="{ row }">
          <button
            type="button"
            class="rounded border border-primary px-2.5 py-1 text-xs font-bold text-primary"
            :data-testid="`ds-attendance-table-details-${row.id}`"
            @click="emit('details', row.id)"
          >
            Ver detalhes
          </button>
        </template>
      </QueueTable>
      <EmptyList v-else :title="emptyTitle" />
      <QueueBulkBar
        v-if="selectedIds.length"
        :selected-count="selectedIds.length"
        :pending-count="0"
        pending-label=""
        :primary-label="`Aprovar selecionados (${selectedIds.length})`"
        @primary="emit('bulk-approve')"
        @clear="emit('update:selectedIds', [])"
      />
    </div>

    <div v-else data-testid="ds-attendance-view-kanban">
      <QueueKanban
        :rows="kanbanRows"
        :columns="kanbanColumns"
        :row-key="rowKey"
        :column-totals="kanbanTotals"
        :column-has-more="kanbanHasMore"
        :column-loading="kanbanLoading"
        @load-more="emit('load-more', $event)"
      >
        <template #card="{ row }">
          <DsAttendanceKanbanCard
            :container="row"
            :now-ms="nowMs"
            @details="emit('details', $event)"
          />
        </template>
      </QueueKanban>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import EmptyList from '@/components/EmptyList.vue'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import AuditoriumExceptionalitiesCardSkeleton from '@/components/designSystem/Skeleton/AuditoriumExceptionalitiesCardSkeleton.vue'
import ModuleViewSlaChip from '@/components/designSystem/QueueViews/ModuleViewSlaChip.vue'
import QueueBulkBar from '@/components/designSystem/QueueViews/QueueBulkBar.vue'
import QueueKanban from '@/components/designSystem/QueueViews/QueueKanban.vue'
import QueueTable from '@/components/designSystem/QueueViews/QueueTable.vue'
import QueueViewSwitcher from '@/components/designSystem/QueueViews/QueueViewSwitcher.vue'
import type { QueueSort, QueueViewMode } from '@/components/designSystem/QueueViews/types'
import { DS_ATTENDANCE_QUEUE_SCREEN_KEY } from '@/constants/ds-attendance-sla.constants'
import type { DSAttendanceContainer } from '@/types/ds-attendance.types'

import type { DsAttendanceGroupBy } from '../../composables/useDsAttendanceQueueFilters'
import { toAttendanceListItem } from '../../utils/attendance-list-item'
import { isBulkApproveEligible } from '../../utils/bulk-approve'
import type { DsAttendanceKanbanColumn } from '../../utils/queue-kanban-columns'
import { formatTableDeadline } from '../../utils/queue-sla-line'
import { DS_ATTENDANCE_TABLE_COLUMNS } from '../../utils/queue-table-columns'
import DsAttendanceListCard from '../DsAttendanceListCard.vue'
import DsAttendanceKanbanCard from './DsAttendanceKanbanCard.vue'
import DsAttendanceSlaLine from './DsAttendanceSlaLine.vue'

/**
 * As três visões de Registro de plantão (spec 2026-10-01 §7.1/§7.2): switcher + "Agrupar por",
 * Lista (cartões de hoje + faixa de SLA, G2-D19), Tabela com seleção em lote (só aprováveis) e
 * Kanban K1/K2 com "Ver mais" por coluna. Sem estado próprio: tudo entra por prop e sai por
 * evento — quem busca, pagina e aprova é o `Index.vue`.
 */
const props = withDefaults(
  defineProps<{
    view: QueueViewMode
    groupBy: DsAttendanceGroupBy
    listRows: DSAttendanceContainer[]
    kanbanColumns: DsAttendanceKanbanColumn[]
    kanbanRows: DSAttendanceContainer[]
    kanbanTotals: Partial<Record<string, number>>
    kanbanHasMore: Partial<Record<string, boolean>>
    kanbanLoading: Partial<Record<string, boolean>>
    /** 1ª carga do recorte no Kanban (skeleton, mockup "carregando"). */
    kanbanInitialLoading?: boolean
    /** Contador e todas as colunas falharam (mockup "erro"). */
    kanbanFailed?: boolean
    nowMs: number | null
    loading: boolean
    error: string | null
    emptyTitle: string
    selectedIds: number[]
    deadlineSorted: boolean
    skeletonCount?: number
  }>(),
  { skeletonCount: 10, kanbanInitialLoading: false, kanbanFailed: false }
)

const emit = defineEmits<{
  'update:view': [view: QueueViewMode]
  'update:groupBy': [groupBy: DsAttendanceGroupBy]
  'update:selectedIds': [ids: number[]]
  'load-more': [columnKey: string]
  details: [id: number | string]
  retry: []
  'sort-by-deadline': []
  'bulk-approve': []
}>()

const GROUP_BY_OPTIONS: Array<{ value: DsAttendanceGroupBy; label: string }> = [
  { value: 'estado', label: 'Estado do prazo' },
  { value: 'etapa', label: 'Etapa' }
]

const DEADLINE_SORT: QueueSort = { key: 'deadline', direction: 'asc' }

const KANBAN_ERROR_MESSAGE = 'Verifique sua conexão e tente novamente.'

/** Mockup "vazio": o recorte carregou e as colunas somam 0. */
const kanbanEmpty = computed(() => {
  const totals = Object.values(props.kanbanTotals)
  return (
    totals.length > 0 &&
    totals.reduce<number>((sum, total) => sum + (total ?? 0), 0) === 0 &&
    props.kanbanRows.length === 0
  )
})

const rowKey = (row: DSAttendanceContainer): number => row.id

const listEntries = computed(() =>
  props.listRows.map((container) => ({ container, item: toAttendanceListItem(container) }))
)

/** "Prazo" da Tabela calculado uma vez por linha (a célula lê data e fase). */
const tableDeadlines = computed(
  () => new Map(props.listRows.map((row) => [row.id, formatTableDeadline(row)]))
)

/** Aprováveis em lote, por id — reaproveita o item já mapeado em `listEntries`. */
const selectableIds = computed(
  () =>
    new Set(
      listEntries.value
        .filter((entry) => isBulkApproveEligible(entry.item))
        .map((entry) => entry.container.id)
    )
)

const isRowSelectable = (row: DSAttendanceContainer): boolean => selectableIds.value.has(row.id)

function onSort(sort: QueueSort): void {
  if (sort.key === 'deadline') emit('sort-by-deadline')
}
</script>
